import React from 'react';
import { FaCog, FaTimes } from 'react-icons/fa';
import { ProductOptionTypeSelectorProps } from './ProductFormTypes';

/**
 * Product Option Type Selector Component
 * Handles option types selection for products (size, steeping, sugar, temperature, etc.)
 */

const ProductOptionTypeSelector: React.FC<ProductOptionTypeSelectorProps> = ({
  product,
  onUpdate,
  optionTypes,
}) => {
  const selectedOptionTypes = product.option_type_ids || [];

  const handleOptionTypeToggle = (optionTypeId: number) => {
    const currentIds = selectedOptionTypes;
    const isSelected = currentIds.includes(optionTypeId);

    let newIds: number[];
    if (isSelected) {
      newIds = currentIds.filter(id => id !== optionTypeId);
    } else {
      newIds = [...currentIds, optionTypeId];
    }

    onUpdate({
      option_type_ids: newIds,
      has_options: newIds.length > 0, // has_options'u otomatik güncelle
    });
  };

  return (
    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-5 rounded-xl border border-orange-100">
      <div className="space-y-4">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-3">
          <FaCog className="text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Seçenek Türleri</h3>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Bu ürün için hangi seçenek türlerinin sunulacağını belirleyin (boyut, dem, şeker, sıcaklık
          vs.)
        </p>

        {/* Option Types List */}
        {optionTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FaCog className="text-gray-300 text-4xl mx-auto mb-3" />
            <p className="text-lg font-medium">Henüz hiç seçenek türü tanımlanmamış</p>
            <p className="text-sm mt-1">Admin panelinden seçenek türleri ekleyebilirsiniz.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {optionTypes.map(optionType => {
              const isSelected = selectedOptionTypes.includes(optionType.id);
              return (
                <div
                  key={optionType.id}
                  onClick={() => handleOptionTypeToggle(optionType.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md transform hover:scale-105 ${
                    isSelected
                      ? 'border-orange-400 bg-orange-50 shadow-md scale-105'
                      : 'border-gray-200 bg-white hover:border-orange-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-3">
                      <h4
                        className={`font-semibold text-sm ${isSelected ? 'text-orange-700' : 'text-gray-700'}`}
                      >
                        {optionType.display_name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        <span className="font-medium">Seçenekler:</span>{' '}
                        {optionType.options.join(', ')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        <span className="font-medium">Varsayılan:</span> {optionType.default_value}
                      </p>
                    </div>
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'border-orange-400 bg-orange-400 shadow-sm'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      {isSelected && <FaTimes className="text-white text-xs rotate-45" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Selected Option Types Summary */}
        {selectedOptionTypes.length > 0 && (
          <div className="mt-5 p-4 bg-orange-100 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-orange-700 font-semibold">
                Seçilen Seçenek Türleri: {selectedOptionTypes.length}
              </p>
              <button
                onClick={() => onUpdate({ option_type_ids: [], has_options: false })}
                className="text-xs text-orange-600 hover:text-orange-800 underline"
              >
                Tümünü Kaldır
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedOptionTypes.map(id => {
                const optionType = optionTypes.find(ot => ot.id === id);
                return optionType ? (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-orange-200 text-orange-700 text-xs rounded-full font-medium"
                  >
                    {optionType.display_name}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleOptionTypeToggle(id);
                      }}
                      className="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </span>
                ) : null;
              })}
            </div>

            {/* Info Text */}
            <div className="mt-3 text-xs text-orange-600">
              <p>
                💡 Müşteriler bu ürünü sipariş ederken yukarıdaki seçenekler arasından tercihleri
                yapabilecekler.
              </p>
            </div>
          </div>
        )}

        {/* No Selection Info */}
        {selectedOptionTypes.length === 0 && optionTypes.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              Hiç seçenek türü seçilmedi. Bu ürün sadece standart haliyle satışa sunulacak.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductOptionTypeSelector;
